import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  RefreshCw, Heart, Image, Building2, Calendar, Users, ChevronRight,
  Palette, FileText, Eye, ListChecks, Check, ExternalLink
} from "lucide-react";

export default function ConsolidatedOverridesView() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("interests");

  const fetchOverrides = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/consolidated-overrides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
      } else {
        toast.error("Failed to fetch overrides");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading overrides");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverrides();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="pt-8 pb-8 text-center">
          <p className="text-gray-500">No override data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Consolidated Overrides</h2>
          <p className="text-gray-500">View all customizations across pilots and events</p>
        </div>
        <Button variant="outline" onClick={fetchOverrides}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <Heart className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.total_custom_interests}</p>
                <p className="text-xs text-gray-500">Custom Interests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Image className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.total_branding_overrides}</p>
                <p className="text-xs text-gray-500">Branding Overrides</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.pilot_profiles_with_overrides?.length || 0}</p>
                <p className="text-xs text-gray-500">Pilots with Overrides</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{data.events_with_overrides}</p>
                <p className="text-xs text-gray-500">Events with Overrides</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="interests" className="flex items-center gap-1">
            <Heart className="w-4 h-4" />
            Custom Interests ({data.total_custom_interests})
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-1">
            <Image className="w-4 h-4" />
            Branding ({data.total_branding_overrides})
          </TabsTrigger>
          <TabsTrigger value="pilots" className="flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            Pilots ({data.pilot_profiles_with_overrides?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Custom Interests Tab */}
        <TabsContent value="interests" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-rose-500" />
                All Custom Interests
              </CardTitle>
              <CardDescription>
                Interest categories added by hosts across all pilots and events
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.custom_interests?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Heart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No custom interests added yet</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {data.custom_interests?.map((interest, idx) => (
                      <div 
                        key={interest.id || idx}
                        className="p-4 rounded-lg border hover:border-rose-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-rose-100 text-rose-700">
                              {interest.label}
                            </Badge>
                            <span className="text-xs text-gray-400">ID: {interest.id}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {interest.sources?.length || 0} source(s)
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {interest.sources?.map((source, sidx) => (
                            <div 
                              key={sidx}
                              className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs"
                            >
                              {source.type === 'pilot_profile' ? (
                                <Building2 className="w-3 h-3 text-blue-500" />
                              ) : (
                                <Calendar className="w-3 h-3 text-emerald-500" />
                              )}
                              <span className="text-gray-700">{source.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-violet-500" />
                Branding Overrides
              </CardTitle>
              <CardDescription>
                Custom logos and backgrounds configured for pilots and events
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.branding_overrides?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No branding overrides configured yet</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {data.branding_overrides?.map((item, idx) => (
                      <div 
                        key={idx}
                        className="p-4 rounded-lg border hover:border-violet-200 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          {/* Preview */}
                          {(item.branding?.cover_image || item.branding?.logo) && (
                            <div className="w-24 h-16 rounded-lg overflow-hidden border bg-gray-100 flex-shrink-0">
                              {item.branding?.cover_image ? (
                                <img 
                                  src={item.branding.cover_image} 
                                  alt="Cover" 
                                  className="w-full h-full object-cover"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ) : item.branding?.logo ? (
                                <img 
                                  src={item.branding.logo} 
                                  alt="Logo" 
                                  className="w-full h-full object-contain p-2"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ) : null}
                            </div>
                          )}
                          
                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {item.type === 'pilot_profile' ? (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Pilot</Badge>
                              ) : (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Event</Badge>
                              )}
                              <span className="font-medium">{item.name}</span>
                              {item.org && (
                                <span className="text-xs text-gray-400">({item.org})</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 space-y-1">
                              {item.branding?.cover_image && (
                                <p className="flex items-center gap-1">
                                  <Image className="w-3 h-3" />
                                  Background: {item.branding.cover_image.slice(0, 40)}...
                                </p>
                              )}
                              {item.branding?.logo && (
                                <p className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  Logo: {item.branding.logo.slice(0, 40)}...
                                </p>
                              )}
                              {item.branding?.background_color && (
                                <p className="flex items-center gap-1">
                                  <Palette className="w-3 h-3" />
                                  Color: {item.branding.background_color}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pilots Tab */}
        <TabsContent value="pilots" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Pilot Profiles with Overrides
              </CardTitle>
              <CardDescription>
                Overview of which pilots have customizations applied
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.pilot_profiles_with_overrides?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No pilot overrides configured yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.pilot_profiles_with_overrides?.map((pilot, idx) => (
                    <div 
                      key={pilot.profile_id || idx}
                      className="flex items-center justify-between p-4 rounded-lg border hover:border-blue-200 transition-colors"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{pilot.name}</span>
                          {pilot.org && (
                            <span className="text-sm text-gray-400">({pilot.org})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          {pilot.has_interests && (
                            <span className="flex items-center gap-1 text-xs text-rose-600">
                              <Heart className="w-3 h-3" />
                              Interests
                            </span>
                          )}
                          {pilot.has_branding && (
                            <span className="flex items-center gap-1 text-xs text-violet-600">
                              <Image className="w-3 h-3" />
                              Branding
                            </span>
                          )}
                          {pilot.has_content && (
                            <span className="flex items-center gap-1 text-xs text-emerald-600">
                              <FileText className="w-3 h-3" />
                              Content
                            </span>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
